import Badge, { ColorType } from '@/app/common/components/Badge'
import { Button } from '@/app/common/components/button/Button'
import { removeUnderscoresAndMakeFirstLetterCapital } from '@/app/pages/payment-approval-rules/utils'
import { DATE_FNS_DEFAULT_DATE_FORMAT } from '@/lib/utils'
import {
  DistributionStatus,
  PurseDistribution,
} from '@/queries/program_purses/schemas'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shad/ui/table'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

const columnHelper = createColumnHelper<PurseDistribution>()

function getStatusColor(status: DistributionStatus): ColorType {
  switch (status) {
    case 'pending':
      return 'Notice'
    case 'processing':
      return 'Informative'
    case 'completed':
      return 'Positive'
    case 'failed':
      return 'Negative'
    default:
      return 'Neutral'
  }
}

function formatUTCDate(dateTimeStr: string) {
  // Parse ISO UTC date string (already in UTC, just format for display)
  const date = parseISO(dateTimeStr)

  // Format the date displaying it as UTC (no timezone conversion)
  const formatted = formatInTimeZone(date, 'UTC', DATE_FNS_DEFAULT_DATE_FORMAT)
  return `${formatted} (UTC)`
}

interface DistributionTableProps {
  distributions: PurseDistribution[]
  onDownloadClick: (distributionId: number) => void
}

export default function DistributionTable({
  distributions,
  onDownloadClick,
}: DistributionTableProps) {
  const columns = [
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => (
        <Badge semanticColor={getStatusColor(info.getValue())}>
          {removeUnderscoresAndMakeFirstLetterCapital(info.getValue())}
        </Badge>
      ),
    }),
    columnHelper.accessor('awarded_students_count', {
      header: 'Awarded Students',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('program_purse_id', {
      header: 'Program Purse ID',
      cell: (info) => {
        const value = info.getValue()
        return value
      },
    }),
    columnHelper.accessor('started_at', {
      header: 'Started At',
      cell: (info) => {
        const value = info.getValue()
        return value ? formatUTCDate(value) : '-'
      },
    }),
    columnHelper.accessor('started_by', {
      header: 'Started By',
      cell: (info) => {
        const value = info.getValue()
        return value ? value : '-'
      },
    }),
    columnHelper.accessor('completed_at', {
      header: 'Completed At',
      cell: (info) => {
        const value = info.getValue()
        return value ? formatUTCDate(value) : '-'
      },
    }),
    columnHelper.accessor('download_url', {
      header: 'Download',
      cell: (info) => {
        const value = info.getValue()
        const hasProcessed = info.row.original.status === 'completed'
        return value ? (
          <Button
            disabled={!hasProcessed}
            variant={'outline'}
            onClick={() => {
              onDownloadClick(info.row.original.distribution_id)
            }}
          >
            Download Report
          </Button>
        ) : (
          'Report not available'
        )
      },
    }),
  ]

  const table = useReactTable({
    data: distributions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (distributions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        No distributions found.
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
